import { Grid, IconButton, Paper } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import PageContainer from "../containers/PageContainer";
import PageTitleCard from "../cards/PageTitleCard";
import PageTitleText from "../texts/PageTitleText";
import { useTranslation } from "react-i18next";
import useFetch from "../hooks/useFetch";
import UserContext from "../contexts/UserContext";
import {
    getUserStatistics,
    getMenuItemStatistics,
    getHourlyStatistics,
    getUtmSourceStatistics,
} from "../config/apiRoutes";
import RefreshIcon from '@mui/icons-material/Refresh';
import { DailyScreenViews, MenuItemClicks, HourlyViews, UtmSources } from "../models/Statistics";
import UserTrafficChart from "../statistics/UserTrafficChart";
import HourlyTrafficChart from "../statistics/HourlyTrafficChart";
import UtmSourceChart from "../statistics/UtmSourceChart";
import KpiCards from "../statistics/KpiCards";
import SnackbarContext from "../contexts/SnackbarContext";
import BigLoader from "../common/BigLoader";
import PrimaryButtonGroup from "../buttons/PrimaryButtonGroup";
import { hasRole } from "../models/User";
import { RoleType } from "../models/Role";
import PrimaryButton from "../buttons/PrimaryButton";
import { useNavigate, useParams } from "react-router";
import { paymentSettingsRoute } from "../config/pageRoutes";
import MenuLikeStatisticTable from "../statistics/MenuLikeStatisticTable";
import ClickCountTable from "../statistics/ClickCountTable";

type Period = 'last7days' | 'last30days';

const StatisticsPage: React.FC = () => {
    const { t } = useTranslation();
    const { lang } = useParams();
    const { selectedRestaurant, user, hasFeature } = useContext(UserContext);
    const snackbar = useContext(SnackbarContext);
    const navigate = useNavigate();

    const [period, setPeriod] = useState<Period>('last30days');
    const [pageViewData, setPageViewData] = useState<DailyScreenViews | null>(null);
    const [menuClickData, setMenuClickData] = useState<MenuItemClicks | null>(null);
    const [hourlyData, setHourlyData] = useState<HourlyViews | null>(null);
    const [utmData, setUtmData] = useState<UtmSources | null>(null);

    const pageViewCaller = useFetch();
    const menuClickCaller = useFetch();
    const hourlyCaller = useFetch();
    const utmCaller = useFetch();

    const hasAccess = hasRole(user, RoleType.OWNER) && hasFeature("ANALYTICS_INSIGHTS");

    const shortName = selectedRestaurant?.restaurantShortName ?? "";

    useEffect(() => {
        if (selectedRestaurant && hasAccess) {
            fetchAll();
        }
    }, [selectedRestaurant]);

    async function fetchAll() {
        await Promise.all([
            fetchPageViews(),
            fetchMenuClicks(),
            fetchHourly(),
            fetchUtmSources(),
        ]);
    }

    async function fetchPageViews() {
        const url = getUserStatistics.replace(":restaurantShortName", shortName);
        const res = await pageViewCaller.call<undefined, DailyScreenViews>("GET", url);
        if (res.ok && res.data) setPageViewData(res.data);
        else snackbar.showMessage(t("statistics.error_fetching_user_statistics"), "error");
    }

    async function fetchMenuClicks() {
        const url = getMenuItemStatistics.replace(":restaurantShortName", shortName);
        const res = await menuClickCaller.call<undefined, MenuItemClicks>("GET", url);
        if (res.ok && res.data) setMenuClickData(res.data);
        else snackbar.showMessage(t("statistics.error_fetching_menu_item_statistics"), "error");
    }

    async function fetchHourly() {
        const url = getHourlyStatistics.replace(":restaurantShortName", shortName);
        const res = await hourlyCaller.call<undefined, HourlyViews>("GET", url);
        if (res.ok && res.data) setHourlyData(res.data);
    }

    async function fetchUtmSources() {
        const url = getUtmSourceStatistics.replace(":restaurantShortName", shortName);
        const res = await utmCaller.call<undefined, UtmSources>("GET", url);
        if (res.ok && res.data) setUtmData(res.data);
    }

    const redirectToPayment = () => {
        navigate(paymentSettingsRoute.path.replace(':lang', lang || 'en'));
    };

    if (!hasAccess) {
        return (
            <PageContainer>
                <PageTitleCard>
                    <PageTitleText>{t("statistics.statistics")}</PageTitleText>
                </PageTitleCard>
                <div className="flex flex-col items-center justify-center mt-20 gap-4">
                    {hasRole(user, RoleType.OWNER) ? (
                        <>
                            <p className="text-lg mb-4 text-primary-dark">
                                {t("statistics.statistics_feature_not_available")}
                            </p>
                            <PrimaryButton variant="primary" onClick={redirectToPayment}>
                                {t('statistics.change_subscription_plan')}
                            </PrimaryButton>
                        </>
                    ) : (
                        <p className="text-lg mb-4">{t("statistics.permission_denied")}</p>
                    )}
                </div>
            </PageContainer>
        );
    }

    const pageViewsForPeriod = pageViewData?.[period] ?? [];
    const prevKey = period === 'last7days' ? 'prev7days' : 'prev30days';
    const prevPageViewsForPeriod = pageViewData?.[prevKey] ?? [];
    const menuClicksForPeriod = menuClickData?.[period] ?? [];
    const mainLoading = pageViewCaller.loading || menuClickCaller.loading;

    return (
        <PageContainer>
            <PageTitleCard>
                <div className="flex items-center justify-between">
                    <PageTitleText>{t("statistics.statistics")}</PageTitleText>
                    <div className="flex items-center gap-2">
                        <PrimaryButtonGroup
                            variant="outlined"
                            fullWidthOnMobile={false}
                            buttons={[
                                { label: t("statistics.last_7_days"), onClick: () => setPeriod('last7days'), isSelected: period === 'last7days' },
                                { label: t("statistics.last_30_days"), onClick: () => setPeriod('last30days'), isSelected: period === 'last30days' },
                            ]}
                        />
                        <IconButton onClick={fetchAll} title={t("common.refresh")} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </div>
                </div>
            </PageTitleCard>

            <div className="mt-4 flex flex-col gap-4 mb-4">

                {/* KPI Cards */}
                <KpiCards
                    pageViews={pageViewsForPeriod}
                    prevPageViews={prevPageViewsForPeriod}
                    menuItemClicks={menuClicksForPeriod}
                    period={period}
                    loading={mainLoading}
                />

                {/* Traffic chart + UTM sources — 2-col on desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Paper elevation={2} className="p-4 md:col-span-2">
                        <div className="pb-3 pt-1 text-xl font-bold text-primary-dark">
                            {t("statistics.user_traffic")}
                        </div>
                        {pageViewCaller.loading ? (
                            <BigLoader />
                        ) : (
                            <UserTrafficChart
                                data={pageViewData ?? { last7days: [], last30days: [] }}
                                period={period}
                            />
                        )}
                    </Paper>

                    <Paper elevation={2} className="p-4">
                        <div className="pb-3 pt-1 text-xl font-bold text-primary-dark">
                            {t("statistics.utm_sources")}
                        </div>
                        {utmCaller.loading ? (
                            <BigLoader />
                        ) : (
                            <UtmSourceChart data={utmData} period={period} />
                        )}
                    </Paper>
                </div>

                {/* Hourly traffic */}
                <Paper elevation={2} className="p-4">
                    <div className="pb-3 pt-1 text-xl font-bold text-primary-dark">
                        {t("statistics.hourly_traffic")}
                    </div>
                    {hourlyCaller.loading ? (
                        <BigLoader />
                    ) : (
                        <HourlyTrafficChart data={hourlyData} period={period} />
                    )}
                </Paper>

                {/* Menu item clicks */}
                <Paper elevation={2} className="p-4">
                    <ClickCountTable period={period} externalData={menuClickData} loading={menuClickCaller.loading} />
                </Paper>

                {/* Ratings */}
                <Paper elevation={2} className="p-4">
                    <Grid container>
                        <Grid item xs>
                            <div className="pb-4 pt-1 text-xl font-bold text-primary-dark">
                                {t("statistics.menu_item_ratings")}
                            </div>
                        </Grid>
                    </Grid>
                    <MenuLikeStatisticTable />
                </Paper>
            </div>
        </PageContainer>
    );
};

export default StatisticsPage;
